import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useUpdateTiebreakerCorrectValue } from '@/api/live-scoring/updateTiebreakerCorrectValue';
import type { TiebreakerScoringQuestionProps } from '@/utils/scoring/adapters/tiebreakerScoringAdapter';

interface TiebreakerScoringQuestionComponentProps extends TiebreakerScoringQuestionProps {
  questionId?: string;
  manualScoring?: boolean;
  onSave?: () => void;
}

export function TiebreakerScoringQuestion({
  questionId,
  questionText,
  questionSubtext,
  correctValue,
  points,
  manualScoring = false,
}: TiebreakerScoringQuestionComponentProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const updateCorrectValue = useUpdateTiebreakerCorrectValue();

  const displayValue =
    correctValue !== null && correctValue !== undefined ? correctValue.toString() : 'Not set';

  const handleEditClick = () => {
    if (manualScoring && questionId) {
      setEditingValue(correctValue?.toString() || '');
      setIsEditing(true);
      setErrorMessage(null);
      setSuccessMessage(null);
    }
  };

  const handleSave = async () => {
    if (!questionId) return;

    const numValue = parseFloat(editingValue);
    if (isNaN(numValue)) {
      setErrorMessage('Please enter a valid number');
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await updateCorrectValue.mutateAsync({
        questionId,
        correctValue: numValue,
      });

      setSuccessMessage('Correct value updated successfully');
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setErrorMessage('Failed to update correct value. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="space-y-4">
      {/* Question Text */}
      <div>
        <span className="text-lg font-semibold">{questionText}</span>
        {questionSubtext && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{questionSubtext}</p>
        )}
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300">{successMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          <span className="text-sm text-red-700 dark:text-red-300">{errorMessage}</span>
        </div>
      )}

      {/* Correct Value Display/Edit */}
      <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Correct Answer</p>
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={editingValue}
                    onChange={e => setEditingValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-9 w-32"
                    autoFocus
                    step="0.5"
                  />
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={handleSave}
                    disabled={updateCorrectValue.isPending}
                    className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700"
                  >
                    {updateCorrectValue.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={updateCorrectValue.isPending}
                    className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-lg font-semibold">{displayValue}</p>
                  {manualScoring && questionId && (
                    <button
                      type="button"
                      onClick={handleEditClick}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      aria-label="Edit correct value"
                    >
                      <Pencil className="h-4 w-4 text-blue-400" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {points > 0 && (
            <Badge variant="outline" className="text-sm">
              {points}pts
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}
